import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Share, MoreHorizontal } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const DISMISS_KEY = 'laliel-install-dismissed';
const DISMISS_DAYS = 14;
const SHOW_DELAY_MS = 8000;

const TEXT = {
  title: {
    az: "L'Aliel-i telefonunuza əlavə edin",
    en: "Add L'Aliel to your phone",
    ru: "Добавьте L'Aliel на телефон",
    tr: "L'Aliel'i telefonunuza ekleyin",
  },
  subtitle: {
    az: 'Bir toxunuşla kolleksiyaya qayıdın',
    en: 'Back to the collection in one tap',
    ru: 'Возвращайтесь к коллекции в одно касание',
    tr: 'Tek dokunuşla koleksiyona dönün',
  },
  install: { az: 'Quraşdır', en: 'Install', ru: 'Установить', tr: 'Yükle' },
  iosStep1: {
    az: 'Aşağıdakı',
    en: 'Tap',
    ru: 'Нажмите',
    tr: 'Aşağıdaki',
  },
  iosOr: { az: 'və ya', en: 'or', ru: 'или', tr: 'veya' },
  iosStep2: {
    az: 'düyməsinə basın, «Paylaş» → «Ana ekrana əlavə et» seçin',
    en: 'then Share → «Add to Home Screen»',
    ru: 'затем «Поделиться» → «На экран Домой»',
    tr: 'dokunun, «Paylaş» → «Ana Ekrana Ekle» seçin',
  },
  close: { az: 'Bağla', en: 'Close', ru: 'Закрыть', tr: 'Kapat' },
};

const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true);

const isIOS = () =>
  typeof navigator !== 'undefined' &&
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !/crios|fxios/i.test(navigator.userAgent); // iOS-da yalnız Safari quraşdıra bilir

const recentlyDismissed = () => {
  try {
    const ts = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
    return ts && Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

/**
 * Android/Chrome: brauzerin öz "quraşdır" pəncərəsini açır.
 * iPhone/Safari: belə pəncərə yoxdur — addımları göstəririk.
 * Artıq quraşdırılıbsa, admin panelindədirsə və ya bağlanıbsa — görünmür.
 */
const InstallPrompt = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const [deferred, setDeferred] = useState(null);
  const [mode, setMode] = useState(null); // 'android' | 'ios' | null
  const [visible, setVisible] = useState(false);

  const tx = (obj) => obj[language] || obj.az;

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return undefined;

    let timer;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setMode('android');
      timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    };

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    if (isIOS()) {
      setMode('ios');
      timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    }

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* yaddaş əlçatan deyil — sükutla keç */
    }
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } finally {
      setDeferred(null);
      setVisible(false);
    }
  };

  if (!visible || !mode || location.pathname.startsWith('/admin')) return null;

  return (
    <div
      className="fixed left-3 right-3 sm:left-auto sm:right-6 sm:w-[380px] z-[45] bg-white border border-gray-200 shadow-xl"
      style={{ bottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      role="dialog"
      aria-label={tx(TEXT.title)}
    >
      <div className="flex items-start gap-3 p-4">
        <img
          src="/icons/icon-192.png"
          alt=""
          className="w-11 h-11 flex-shrink-0"
        />

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 leading-snug">{tx(TEXT.title)}</p>

          {mode === 'android' ? (
            <p className="text-xs text-gray-500 mt-0.5">{tx(TEXT.subtitle)}</p>
          ) : (
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
              {tx(TEXT.iosStep1)}{' '}
              <MoreHorizontal className="inline w-4 h-4 -mt-0.5 text-gray-800" strokeWidth={2} />{' '}
              {tx(TEXT.iosOr)}{' '}
              <Share className="inline w-3.5 h-3.5 -mt-0.5 text-gray-800" strokeWidth={1.75} />{' '}
              {tx(TEXT.iosStep2)}
            </p>
          )}

          {mode === 'android' && (
            <button
              type="button"
              onClick={install}
              className="mt-3 px-4 py-2 bg-black text-white text-xs tracking-wider hover:bg-gray-800 transition-colors"
            >
              {tx(TEXT.install).toUpperCase()}
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={dismiss}
          aria-label={tx(TEXT.close)}
          className="flex items-center justify-center w-9 h-9 -mt-1 -mr-1 text-gray-400 hover:text-gray-900 transition-colors flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
